/**
 * メールアドレス詳細（05-ui.md §8.4）。
 *
 * タブ: 基本設定 / 転送 / 自動応答 / メールソフト設定。
 * 迷惑メールの判定はドメイン単位の設定（`/spam`）に集約しているので、
 * ここからはそちらへ誘導するだけにしている（設定が 2 か所にあると
 * どちらが効いているのか分からなくなるため）。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableCode,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  SwitchRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  UsageBar,
  formatBytes,
  formatDateTime,
} from '@mail-sv/ui';
import { ArrowLeft, ExternalLink, FileDown, KeyRound, Mail, Plus, Printer, Trash2 } from 'lucide-react';
import type { SpamAction } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink, useAppNavigate, useRouteParams } from '../lib/nav.js';
import { Async, ErrorAlert, LoadingBlock } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { PasswordField } from '../components/password-field.js';
import { ConfirmTextDialog } from '../components/confirm-text-dialog.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import {
  MfaEnrollmentNotice,
  ReauthDialog,
  needsMfaEnrollment,
  needsReauth,
} from '../components/reauth-dialog.js';
import { HelpNote } from '../components/help.js';
import { MailClientTable } from './mail-clients.js';
import { bytesToGib, formatQuota, gibToBytes } from '../lib/format.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import {
  fromAutoresponderDraft,
  toAutoresponderDraft,
  type AutoresponderDraft,
} from '../lib/autoresponder.js';
import { openPendingTab } from '../lib/open-tab.js';
import type {
  Autoresponder,
  CursorPage,
  Forwarder,
  Mailbox,
  SenderAclEntry,
  WebmailLink,
  WebmailPassword,
} from '../lib/api-types.js';

export function MailboxDetailPage() {
  const params = useRouteParams();
  const mailboxId = params.mailboxId ?? '';

  /**
   * 猶予つきで削除した直後の状態（R128）。
   *
   * **復元（`POST /mailboxes/:id/restore`）は API にあるのに、押せる場所が
   * どこにも無かった。** 削除すると `GET /mailboxes`（一覧）も
   * `GET /mailboxes/:id`（詳細）も `deleted_at is null` で絞るので、
   * 契約者は消したメールボックスの ID を二度と見られない。つまり
   * 「30 日の猶予」は実質「消えるまでの待ち時間」でしかなかった。
   *
   * ここでは**削除した直後のこの画面から戻せる**ようにする。ページを離れると
   * 復元の入口は失われるので、そのことも画面に明記する
   * （一覧に「削除予定」を出すには API 側の対応が要る＝申し送り）。
   */
  const [deleted, setDeleted] = React.useState<{ address: string; purgeAfter: string } | null>(null);

  const mailbox = useQuery({
    queryKey: qk.mailbox(mailboxId),
    queryFn: () => api.get<Mailbox>(`/mailboxes/${encodeURIComponent(mailboxId)}`),
    // 削除後は 404 になる。復元パネルを出している間は取りに行かない。
    enabled: mailboxId !== '' && deleted === null,
  });

  if (deleted) {
    return (
      <>
        <PageHeader
          title={deleted.address}
          description="このメールアドレスを削除しました。"
          breadcrumbs={[{ label: 'メールアドレス', href: '/mailboxes' }, { label: deleted.address }]}
        />
        <DeletedPanel
          mailboxId={mailboxId}
          address={deleted.address}
          purgeAfter={deleted.purgeAfter}
          onRestored={() => setDeleted(null)}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mailbox.data?.address ?? 'メールアドレス'}
        description="このメールアドレスの設定を変更できます。"
        breadcrumbs={[
          { label: 'メールアドレス', href: '/mailboxes' },
          { label: mailbox.data?.address ?? '' },
        ]}
        actions={
          <Button asChild variant="outline" size="sm">
            <AppLink to="/mailboxes">
              <ArrowLeft aria-hidden />
              一覧に戻る
            </AppLink>
          </Button>
        }
      />

      <Async query={mailbox} lines={6}>
        {(mb) => (
          <div className="space-y-5">
            <Card>
              <CardContent className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">状態</p>
                  <StatusBadge status={mb.status} dot />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <UsageBar
                    used={mb.usedBytes}
                    // null は「プラン既定を継承」。0 を渡すと無制限扱いで描かれる。
                    total={mb.quotaBytes ?? 0}
                    label="使用容量"
                    formatValue={(n) => formatBytes(n)}
                  />
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="basic">
              <TabsList>
                <TabsTrigger value="basic">基本設定</TabsTrigger>
                <TabsTrigger value="forward">転送</TabsTrigger>
                <TabsTrigger value="autoreply">自動応答</TabsTrigger>
                <TabsTrigger value="senders">送信できる差出人</TabsTrigger>
                <TabsTrigger value="clients">メールソフト設定</TabsTrigger>
              </TabsList>

              <TabsContent value="basic">
                <BasicTab mailbox={mb} onDeleted={setDeleted} />
              </TabsContent>
              <TabsContent value="forward">
                <ForwardTab mailbox={mb} />
              </TabsContent>
              <TabsContent value="autoreply">
                <AutoreplyTab mailbox={mb} />
              </TabsContent>
              <TabsContent value="senders">
                <SendersTab mailbox={mb} />
              </TabsContent>
              <TabsContent value="clients">
                <ClientsTab mailbox={mb} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Async>
    </>
  );
}

/* ------------------------------------------------------------ 削除後の復元 */

/**
 * 猶予つき削除の直後だけ出す復元パネル（R128）。
 *
 * 復元は `mailbox_update` の再実行なので 202 が返る。実体はノード上に残って
 * いて、必要なのは desired-state の配り直しだけ。
 */
function DeletedPanel({
  mailboxId,
  address,
  purgeAfter,
  onRestored,
}: {
  mailboxId: string;
  address: string;
  purgeAfter: string;
  onRestored: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useAppNavigate();

  const restore = useMutation({
    mutationFn: () =>
      api.post<{ jobId: string }>(`/mailboxes/${encodeURIComponent(mailboxId)}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.mailboxes() });
      void queryClient.invalidateQueries({ queryKey: qk.mailbox(mailboxId) });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      onRestored();
    },
  });

  return (
    <div className="space-y-5">
      <Alert variant="warning">
        <AlertTitle>{address} を削除しました</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            このアドレス宛のメールは受信できなくなりました。保存されているメールは
            <strong>{formatDateTime(purgeAfter)}</strong> まで保持され、その後に完全に削除されます。
          </p>
          <p className="text-xs">
            <strong>この画面を離れると、元に戻す操作はできなくなります。</strong>
            誤って削除された場合は、いま復元してください。
          </p>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">元に戻す</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            復元すると、このアドレスは削除前の設定のまま再び使えるようになります。
            保存されているメールもそのまま残ります。
          </p>
          {restore.isError ? <ErrorAlert error={restore.error} /> : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => restore.mutate()} disabled={restore.isPending}>
              {restore.isPending ? '復元しています…' : 'このメールアドレスを復元する'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/mailboxes', replace: true })}
              disabled={restore.isPending}
            >
              一覧に戻る
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- 基本設定 */

function BasicTab({
  mailbox,
  onDeleted,
}: {
  mailbox: Mailbox;
  /** 猶予つき削除に成功したときだけ呼ぶ。即時削除は戻せないので一覧へ帰す。 */
  onDeleted: (deleted: { address: string; purgeAfter: string }) => void;
}) {
  const queryClient = useQueryClient();
  const org = useOrg();
  const navigate = useAppNavigate();
  const { user } = useAuth();
  const canWrite = canManageOrg(user);

  // `displayName` は未設定なら null。制御コンポーネントに null を渡すと
  // React が uncontrolled 扱いに切り替えてしまうので、必ず空文字にする。
  const [displayName, setDisplayName] = React.useState(mailbox.displayName ?? '');
  const [quotaGib, setQuotaGib] = React.useState(bytesToGib(mailbox.quotaBytes ?? 0));
  const [password, setPassword] = React.useState('');
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  /*
   * **即時削除（`?purge=true`）。** 30 日の猶予は「誤って消した」を救うための
   * ものだが、退職者のアカウント・情報漏えい対応・本人からの消去請求には
   * 「30 日お待ちください」としか答えられない状態になっていた。
   * 取り返しがつかないので、既定は猶予つきのまま。明示的に選んだときだけ即時にする。
   */
  const [immediate, setImmediate] = React.useState(false);

  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qk.mailbox(mailbox.id) });
    void queryClient.invalidateQueries({ queryKey: qk.mailboxes() });
  }, [queryClient, mailbox.id]);

  // PATCH のリクエスト形（API の `updateSchema`）。`status` は受け付けない
  // ——契約者が操作できるのは active ⇄ disabled だけなので、真偽値の
  // `enabled` として送る。`suspended` は運営側の状態で触れない。
  const save = useMutation({
    mutationFn: (body: {
      displayName?: string | null;
      quotaBytes?: number | null;
      imapEnabled?: boolean;
      pop3Enabled?: boolean;
      submissionEnabled?: boolean;
      sieveEnabled?: boolean;
      webmailEnabled?: boolean;
      spamAction?: SpamAction;
      enabled?: boolean;
    }) => api.patch<{ jobId: string }>(`/mailboxes/${encodeURIComponent(mailbox.id)}`, body),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: (pw: string) =>
      api.post<{ jobId: string }>(`/mailboxes/${encodeURIComponent(mailbox.id)}/password`, {
        password: pw,
      }),
    onSuccess: () => {
      setPassword('');
      invalidate();
    },
  });

  /**
   * メール本文の書き出し（R56 / R101 / A163）。202 で受け付けだけ返る。
   * 実際に mbox を作るのはノード側なので、ここでは「受け付けた」以上のことは
   * 言わない（`deduplicated` なら連打で二重に走ってはいない、という意味）。
   */
  const exportMail = useMutation({
    mutationFn: () =>
      api.post<{ exportId: string; status: string; commandId: string; deduplicated: boolean }>(
        `/mailboxes/${encodeURIComponent(mailbox.id)}/export`,
      ),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.del<{ jobId: string; purgeAfter: string; immediate: boolean }>(
        // 即時削除はサーバーが**確認のためアドレスの完全一致**を要求する
        // （boolean 1 つで実体を消せる API にはしない、という設計）。
        immediate
          ? `/mailboxes/${encodeURIComponent(mailbox.id)}?purge=true&confirm=${encodeURIComponent(mailbox.address)}`
          : `/mailboxes/${encodeURIComponent(mailbox.id)}`,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: qk.mailboxes() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      /*
       * **猶予つき削除では一覧へ飛ばさない。** 飛ばすと、削除された
       * メールボックスは一覧にも詳細にも出てこなくなり（どちらも
       * `deleted_at is null` で絞る）、復元 API を呼ぶための ID を
       * 契約者が知る手段が消える。誤って消したときに戻せることが
       * 30 日猶予の存在理由なので、その場で戻せる画面に切り替える。
       */
      if (result.immediate) {
        navigate({ to: '/mailboxes', replace: true });
        return;
      }
      onDeleted({ address: mailbox.address, purgeAfter: result.purgeAfter });
    },
  });

  const quotaMaxGib = bytesToGib(org.data?.plan.limits.mailboxQuotaBytesMax ?? 0);

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本情報</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <KeyValue
            items={[
              { key: 'メールアドレス', value: mailbox.address },
              { key: 'ドメイン', value: mailbox.domainName || '—' },
              { key: '作成日', value: formatDateTime(mailbox.createdAt) },
              {
                key: '最終ログイン',
                value: mailbox.lastLoginAt ? formatDateTime(mailbox.lastLoginAt) : 'まだありません',
              },
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">表示名と容量</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ displayName, quotaBytes: gibToBytes(quotaGib) });
            }}
          >
            <FormAlert error={save.error} />
            <Field
              label="表示名"
              htmlFor="mb-display"
              hint="送信したメールの差出人名として表示されます。"
            >
              <Input
                id="mb-display"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={!canWrite}
              />
            </Field>
            <Field
              label="容量の上限"
              htmlFor="mb-quota"
              hint={
                org.data
                  ? `プランの上限は 1 アドレスあたり ${formatQuota(org.data.plan.limits.mailboxQuotaBytesMax)} です。`
                  : undefined
              }
            >
              <div className="flex items-center gap-2">
                <Input
                  id="mb-quota"
                  type="number"
                  min={1}
                  max={quotaMaxGib > 0 ? quotaMaxGib : undefined}
                  value={quotaGib}
                  onChange={(e) => setQuotaGib(Number(e.target.value))}
                  className="w-28"
                  disabled={!canWrite}
                />
                <span className="text-sm text-muted-foreground">GB</span>
              </div>
            </Field>
            {canWrite ? <SubmitBar submitLabel="保存する" pending={save.isPending} /> : null}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">利用状態</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <SwitchRow
            label="このメールアドレスを使う"
            description="オフにすると、送受信とログインができなくなります。保存済みのメールは残ります。"
            checked={mailbox.status !== 'disabled'}
            disabled={!canWrite || save.isPending || mailbox.status === 'suspended'}
            onCheckedChange={(checked) => save.mutate({ enabled: checked })}
          />
          <SwitchRow
            label="Webメールを使えるようにする"
            description="ブラウザでメールを読み書きできるようにします。"
            checked={mailbox.webmailEnabled}
            disabled={!canWrite || save.isPending}
            onCheckedChange={(checked) => save.mutate({ webmailEnabled: checked })}
          />
        </CardContent>
      </Card>

      {/*
       * **接続方法ごとの ON/OFF。**
       *
       * API（`updateSchema`）は最初からこの 4 つを受け付けていたのに、画面が
       * `webmailEnabled` しか送っていなかった。とくに **POP3 は既定が false**
       * なので、画面から変えられない間は契約者が POP3 を一切使えなかった
       * （「メールソフト設定」の案内どおりに設定しても必ず認証で弾かれる）。
       *
       * IMAP と送信（SMTP）を両方切ると、メールソフトからは完全に使えなくなる。
       * 押した結果がそれと分かるよう、説明にそのことを書いておく。
       */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">接続できる方法</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <SwitchRow
            label="IMAP でメールを読む"
            description="サーバーにメールを置いたまま、複数の端末から同じ状態で読めます。通常はオンのままにしてください。"
            checked={mailbox.imapEnabled}
            disabled={!canWrite || save.isPending}
            onCheckedChange={(checked) => save.mutate({ imapEnabled: checked })}
          />
          <SwitchRow
            label="POP3 でメールを受信する"
            description="端末にメールを取り込む古い方式です。使うメールソフトが POP3 しか対応していない場合にオンにしてください。"
            checked={mailbox.pop3Enabled}
            disabled={!canWrite || save.isPending}
            onCheckedChange={(checked) => save.mutate({ pop3Enabled: checked })}
          />
          <SwitchRow
            label="メールソフトから送信する（SMTP）"
            description="オフにすると、このアドレスからメールソフト経由で送信できなくなります（受信は続きます）。"
            checked={mailbox.submissionEnabled}
            disabled={!canWrite || save.isPending}
            onCheckedChange={(checked) => save.mutate({ submissionEnabled: checked })}
          />
          <SwitchRow
            label="振り分けルールを使う（Sieve）"
            description="Webメールで設定した自動振り分けを有効にします。"
            checked={mailbox.sieveEnabled}
            disabled={!canWrite || save.isPending}
            onCheckedChange={(checked) => save.mutate({ sieveEnabled: checked })}
          />
        </CardContent>
      </Card>

      {/*
       * **迷惑メールと判定されたときの動作。**
       * `reject`（受け取らない）は誤判定のときにメールが完全に失われるので、
       * 選ばせる前に必ずその旨を書く。既定は迷惑メールフォルダへの移動。
       */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">迷惑メールと判定されたとき</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field
            label="この アドレス宛の動作"
            hint="ドメイン全体の設定より、こちらが優先されます。"
          >
            <Select
              value={mailbox.spamAction}
              disabled={!canWrite || save.isPending}
              onValueChange={(v) => save.mutate({ spamAction: v as SpamAction })}
            >
              <SelectTrigger className="max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="move_to_junk">迷惑メールフォルダへ移動（推奨）</SelectItem>
                <SelectItem value="tag">件名に印を付けて受信箱に入れる</SelectItem>
                <SelectItem value="reject">受け取らない</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {mailbox.spamAction === 'reject' ? (
            <Alert variant="warning">
              <AlertTitle>誤判定されたメールは失われます</AlertTitle>
              <AlertDescription>
                「受け取らない」を選ぶと、迷惑メールと判定されたメールはサーバーに残りません。
                大切なメールが誤って判定された場合でも、あとから確認できません。
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">パスワードの再設定</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (password.length >= 8) resetPassword.mutate(password);
            }}
          >
            <FormAlert error={resetPassword.error} />
            {resetPassword.isSuccess ? (
              <Alert variant="success">
                <AlertTitle>パスワードを変更しました</AlertTitle>
                <AlertDescription>
                  メールソフトに設定しているパスワードも変更してください。変更するまで送受信できません。
                </AlertDescription>
              </Alert>
            ) : null}
            <PasswordField value={password} onChange={setPassword} />
            <SubmitBar
              submitLabel="パスワードを変更する"
              pending={resetPassword.isPending}
              disabled={!canWrite || password.length < 8}
            />
          </form>
        </CardContent>
      </Card>

      {/*
       * **メール本文の持ち出し（`POST /mailboxes/:id/export`）。**
       *
       * 利用規約 第 8 条は「解約後 30 日間データを取得可能な状態で保持」と
       * 掲示しているのに、本文を取り出す導線が製品のどこにも無かった。
       * `/exports` の「メールボックス」はアドレス・容量の**一覧（メタデータ）**で、
       * 本文は 1 通も含まれない。ここが本文（mbox）の唯一の入口。
       *
       * 生成はノード側が行うので、完了は `/exports` の履歴で確認する。
       * 押した直後に落ちてくるものではないことを画面で明言する。
       */}
      {canWrite ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">メールの持ち出し（mbox）</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              このアドレスに保存されているメールを 1 つのファイル（mbox 形式）にまとめて書き出します。
              他社サービスへの移行や、解約前のお持ち出しにご利用ください。
            </p>
            <FormAlert error={exportMail.error} />
            {exportMail.isSuccess ? (
              <Alert variant="success">
                <AlertTitle>
                  {exportMail.data?.deduplicated
                    ? 'すでに書き出しの準備を進めています'
                    : '書き出しを受け付けました'}
                </AlertTitle>
                <AlertDescription>
                  準備ができると「データの書き出し」の一覧に表示されます。メールの量によっては
                  数時間かかることがあります。作成されたファイルは 7 日で取得できなくなります。
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => exportMail.mutate()}
                disabled={exportMail.isPending}
              >
                <FileDown aria-hidden />
                {exportMail.isPending ? '受付中…' : 'メールを書き出す'}
              </Button>
              <Button asChild variant="ghost" size="sm">
                <AppLink to="/exports">書き出しの状況を見る</AppLink>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canWrite ? (
        <Card className="border-destructive/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">メールアドレスの削除</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              削除すると、このアドレス宛のメールは受信できなくなります。保存済みのメールは
              <strong>30 日間だけ保持</strong>され、その後に完全に削除されます。
            </p>

            <label className="flex items-start gap-2 rounded-md border border-destructive/40 p-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 size-4"
                checked={immediate}
                onChange={(e) => setImmediate(e.target.checked)}
              />
              <span>
                <span className="font-medium">保存されているメールも今すぐ削除する</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  退職された方のアドレスや、ご本人からの消去のご請求に対応する場合にお使いください。
                  <strong>元に戻せません。</strong>
                </span>
              </span>
            </label>

            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 aria-hidden />
              {immediate ? 'このメールアドレスを今すぐ削除する' : 'このメールアドレスを削除する'}
            </Button>
            {remove.isError ? <ErrorAlert error={remove.error} /> : null}
          </CardContent>

          <ConfirmTextDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title={`${mailbox.address} を削除します`}
            confirmText={mailbox.address}
            consequences={
              immediate
                ? [
                    'このアドレス宛のメールは受信できなくなります（送信者にエラーが返ります）。',
                    'サーバーに保存されているメールを今すぐ削除します。30 日の保持は行いません。',
                    '削除後に元へ戻すことはできません。',
                    'Webメールとメールソフトからログインできなくなります。',
                  ]
                : [
                    'このアドレス宛のメールは受信できなくなります（送信者にエラーが返ります）。',
                    'サーバーに保存されているメールは 30 日間保持され、その後に削除されます。',
                    'Webメールとメールソフトからログインできなくなります。',
                  ]
            }
            pending={remove.isPending}
            error={remove.error}
            onConfirm={() => remove.mutate()}
          />
        </Card>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------- 転送 */

function ForwardTab({ mailbox }: { mailbox: Mailbox }) {
  const queryClient = useQueryClient();
  const [destination, setDestination] = React.useState('');
  const [keepCopy, setKeepCopy] = React.useState(true);

  const list = useQuery({
    queryKey: qk.forwarders({ mailboxId: mailbox.id }),
    queryFn: () =>
      api.get<CursorPage<Forwarder>>(
        `/forwarders?mailboxId=${encodeURIComponent(mailbox.id)}&limit=100`,
      ),
  });

  const add = useMutation({
    // 202 + `{forwardId, verified:false}`。同意確認メールが届くまで配送には効かない。
    mutationFn: () =>
      api.post<{ forwardId: string; verified: boolean }>('/forwarders', {
        mailboxId: mailbox.id,
        destination: destination.trim(),
        keepCopy,
      }),
    onSuccess: () => {
      setDestination('');
      void queryClient.invalidateQueries({ queryKey: qk.forwarders() });
      void queryClient.invalidateQueries({ queryKey: qk.mailbox(mailbox.id) });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/forwarders/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.forwarders() });
      void queryClient.invalidateQueries({ queryKey: qk.mailbox(mailbox.id) });
    },
  });

  const items = list.data?.items ?? [];
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(destination.trim());

  return (
    <div className="space-y-5">
      <HelpNote title="転送とは">
        このアドレス宛に届いたメールを、別のメールアドレスへ自動で送る機能です。
        「コピーを残す」をオンにすると、このアドレスにも保存されます（オフにすると転送先だけに届きます）。
      </HelpNote>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">転送先の追加</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (emailOk) add.mutate();
            }}
          >
            <FormAlert error={add.error} />
            <Field
              label="転送先のメールアドレス"
              htmlFor="fwd-dest"
              required
              error={destination.length > 0 && !emailOk ? 'メールアドレスの形式が正しくありません' : undefined}
            >
              <Input
                id="fwd-dest"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="tenten@example.com"
                autoComplete="off"
              />
            </Field>
            <SwitchRow
              label="転送元にもコピーを残す"
              description="オフにすると、このアドレスにはメールが保存されません。"
              checked={keepCopy}
              onCheckedChange={setKeepCopy}
            />
            <SubmitBar submitLabel="転送先を追加" pending={add.isPending} disabled={!emailOk} />
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">現在の転送設定</CardTitle>
        </CardHeader>
        <CardContent>
          {list.isPending ? (
            <LoadingBlock lines={2} />
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">転送設定はありません。</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{f.destination}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.keepCopy ? 'コピーを残す' : '転送のみ（元には残さない）'}
                      {f.srsEnabled ? ' / 送信元書き換え（SRS）有効' : ''}
                    </p>
                    {!f.verified ? (
                      <p className="text-xs text-warning">
                        転送先での確認待ちです。確認が終わるまで転送は始まりません。
                      </p>
                    ) : null}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove.mutate(f.id)}
                    loading={remove.isPending}
                  >
                    <Trash2 aria-hidden />
                    削除
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Alert variant="info">
        <AlertTitle>転送先で迷惑メール扱いされる場合</AlertTitle>
        <AlertDescription>
          転送は、元の送信者になりすました形になるため、転送先（Gmail など）で迷惑メールと
          判定されることがあります。本サービスでは送信元アドレスを自動的に書き換えて
          （SRS）この問題を軽減していますが、確実ではありません。
        </AlertDescription>
      </Alert>
    </div>
  );
}

/* ---------------------------------------------------------------- 自動応答 */

function AutoreplyTab({ mailbox }: { mailbox: Mailbox }) {
  const queryClient = useQueryClient();

  const current = useQuery({
    queryKey: qk.autoresponder(mailbox.id),
    queryFn: () =>
      api.get<Autoresponder>(`/mailboxes/${encodeURIComponent(mailbox.id)}/autoresponder`),
  });

  // API の形（bodyText / startsAt / repeatIntervalSeconds）と画面の下書き形は
  // 別物なので、境界で `lib/autoresponder.ts` に変換を閉じ込める。
  const [draft, setDraft] = React.useState<AutoresponderDraft | null>(null);
  React.useEffect(() => {
    if (current.data && draft === null) setDraft(toAutoresponderDraft(current.data));
  }, [current.data, draft]);

  const save = useMutation({
    mutationFn: (body: AutoresponderDraft) =>
      api.put<{ jobId: string }>(
        `/mailboxes/${encodeURIComponent(mailbox.id)}/autoresponder`,
        fromAutoresponderDraft(body),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.autoresponder(mailbox.id) });
      void queryClient.invalidateQueries({ queryKey: qk.mailbox(mailbox.id) });
    },
  });

  if (current.isPending || draft === null) return <LoadingBlock lines={4} />;
  if (current.isError) return <ErrorAlert error={current.error} onRetry={() => current.refetch()} />;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">自動応答（不在通知）</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(draft);
          }}
        >
          <FormAlert error={save.error} />
          {save.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>保存しました。</AlertDescription>
            </Alert>
          ) : null}

          <SwitchRow
            label="自動応答を有効にする"
            description="このアドレス宛にメールが届いたとき、あらかじめ用意した文面を自動で返信します。"
            checked={draft.enabled}
            onCheckedChange={(enabled) => setDraft({ ...draft, enabled })}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="開始日時（任意）" htmlFor="ar-start">
              <Input
                id="ar-start"
                type="datetime-local"
                value={toLocalInput(draft.startAt)}
                onChange={(e) => setDraft({ ...draft, startAt: fromLocalInput(e.target.value) })}
              />
            </Field>
            <Field label="終了日時（任意）" htmlFor="ar-end">
              <Input
                id="ar-end"
                type="datetime-local"
                value={toLocalInput(draft.endAt)}
                onChange={(e) => setDraft({ ...draft, endAt: fromLocalInput(e.target.value) })}
              />
            </Field>
          </div>

          <Field label="件名" htmlFor="ar-subject" required>
            <Input
              id="ar-subject"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              placeholder="【自動返信】お問い合わせありがとうございます"
            />
          </Field>

          <Field label="本文" htmlFor="ar-body" required>
            <textarea
              id="ar-body"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              rows={8}
              className="flex w-full rounded-md border border-input bg-panel-elevated px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={'お問い合わせありがとうございます。\n担当者より 2 営業日以内にご連絡いたします。'}
            />
          </Field>

          <SwitchRow
            label="同じ相手には 1 日 1 回だけ返信する"
            description="やり取りが続いたときに、自動返信が何度も送られるのを防ぎます。"
            checked={draft.oncePerDay}
            onCheckedChange={(oncePerDay) => setDraft({ ...draft, oncePerDay })}
          />

          <SubmitBar
            submitLabel="保存する"
            pending={save.isPending}
            disabled={draft.enabled && (draft.subject.trim() === '' || draft.body.trim() === '')}
          />
        </form>
      </CardContent>
    </Card>
  );
}

/** ISO 文字列 → `datetime-local` の値。 */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* ------------------------------------------------ Webメールのパスワード開示 */

/**
 * Webメール専用パスワードの取り出し（`POST /mailboxes/:id/webmail/password`）。
 *
 * **これが無いと Webメールに誰もログインできなかった。** aurora 側の
 * パスワードは KMS からの導出値で、利用者も運営者もその値を知る手段が
 * 無い。SSO のディープリンク（「Webメールを開く」）が使えない場面
 * （ブックマークから直接開いた・別端末で開いた）では、入力すべき文字列が
 * 製品内のどこにも存在しない状態だった。API は先に在ったのに、
 * 押せる場所が 1 か所も無かった。
 *
 * サーバ側の統制をそのまま画面に写す:
 *   - **fresh-auth 必須。** `REAUTH_REQUIRED` を受けたら再認証ダイアログを
 *     出して、成功したら同じ操作を撃ち直す。導線が無いとこの操作は
 *     「ログインし直す」以外の方法で永久に実行できない。
 *   - `org.mailbox.write` は `IMPERSONATION_FORBIDDEN_PERMISSIONS` なので、
 *     代理ログイン中の運営者には届かない（通信の秘密の統制）。
 *   - 値は**保存されない**（要求のたびに導出し直す）。画面にも残さず、
 *     閉じれば消える。
 */
function WebmailPasswordPanel({ mailbox }: { mailbox: Mailbox }) {
  const [reauthOpen, setReauthOpen] = React.useState(false);
  const [revealed, setRevealed] = React.useState<WebmailPassword | null>(null);

  const reveal = useMutation({
    mutationFn: () =>
      api.post<WebmailPassword>(
        `/mailboxes/${encodeURIComponent(mailbox.id)}/webmail/password`,
      ),
    onSuccess: (data) => setRevealed(data),
    onError: (error) => {
      // 5 分の期限切れはダイアログで解ける。未登録は解けないので下で案内する。
      if (needsReauth(error)) setReauthOpen(true);
    },
  });

  if (revealed) {
    return (
      <div className="space-y-3 rounded-lg border border-warning/40 bg-warning/5 p-4">
        <Alert variant="warning">
          <AlertTitle>この画面を閉じると再表示できません</AlertTitle>
          <AlertDescription>{revealed.note}</AlertDescription>
        </Alert>
        <CopyableCode label="ログイン ID" value={revealed.address} />
        <CopyableCode label="Webメールのパスワード" value={revealed.password} />
        <Button type="button" variant="outline" size="sm" onClick={() => setRevealed(null)}>
          閉じる
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        別の端末やブックマークから Webメールを開く場合は、Webメール専用のパスワードが必要です。
      </p>
      {needsMfaEnrollment(reveal.error) ? (
        <MfaEnrollmentNotice purpose="Webメールのパスワードの表示" />
      ) : (
        <>
          {reveal.isError && !needsReauth(reveal.error) ? (
            <ErrorAlert error={reveal.error} />
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={reveal.isPending}
            onClick={() => reveal.mutate()}
          >
            <KeyRound aria-hidden />
            Webメールのパスワードを表示する
          </Button>
        </>
      )}

      <ReauthDialog
        open={reauthOpen}
        onOpenChange={setReauthOpen}
        purpose="Webメールのパスワードの表示"
        onSuccess={() => reveal.mutate()}
      />
    </div>
  );
}

/* ---------------------------------------------------- 送信できる差出人（A308） */

/**
 * このメールボックスが差出人（From / MAIL FROM）にできるアドレス。
 *
 * **API 3 本は先に在ったのに、画面が無いままだった。** データプレーンは
 * submission の MAIL FROM を**デフォルト拒否**（`smtpd_sender_login_maps` +
 * `reject_sender_login_mismatch`）にしているので、`sender_acl_entries` に
 * 行が入らない限り送信は 550 で弾かれる。行を作るのはドメイン改名に追随する
 * トリガ（＝自分自身のアドレス）だけだったため、
 * **「`info@` 宛に届いたメールに `info@` の差出人で返信できない」**という、
 * レンタルメールサーバーとしては目立つ欠落が残っていた。
 *
 * 自動で入った行（`autoGenerated`）は削除できない。消せると自分の名前で
 * 送信できなくなり、しかも原因の分かりにくい 550 になる。API も 409 を返し、
 * DB のトリガ（`t10_sender_acl_protect`）も同じことを止めている。
 */
function SendersTab({ mailbox }: { mailbox: Mailbox }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canWrite = canManageOrg(user);
  const [sender, setSender] = React.useState('');

  const list = useQuery({
    queryKey: qk.mailboxSenders(mailbox.id),
    queryFn: () =>
      api.get<{ items: SenderAclEntry[]; total: number }>(
        `/mailboxes/${encodeURIComponent(mailbox.id)}/senders`,
      ),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: qk.mailboxSenders(mailbox.id) });

  const add = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/mailboxes/${encodeURIComponent(mailbox.id)}/senders`, {
        sender: sender.trim().toLowerCase(),
      }),
    onSuccess: async () => {
      setSender('');
      await invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (entryId: string) =>
      api.del<unknown>(
        `/mailboxes/${encodeURIComponent(mailbox.id)}/senders/${encodeURIComponent(entryId)}`,
      ),
    onSuccess: () => invalidate(),
  });

  const items = list.data?.items ?? [];
  // `user@example.jp` か `@example.jp`。判定の正はサーバ側なので、
  // ここは往復を 1 回減らすためだけの前段チェック。
  const value = sender.trim().toLowerCase();
  const invalid = !/^@?[^@\s]*@?[^@\s.]+(\.[^@\s.]+)+$/.test(value) || value.length < 3;

  return (
    <div className="space-y-5">
      <HelpNote title="差出人として使えるアドレスを増やす">
        なりすまし送信を防ぐため、メールを送るときの差出人（From）は既定でこのメールアドレス自身に限られます。
        <code className="mx-1 font-mono">info@</code>
        のような共有アドレス宛に届いたメールへ、そのアドレスの差出人で返信したい場合は、
        ここに追加してください。指定できるのはお客様のドメインのアドレスだけです。
      </HelpNote>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">許可されている差出人</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {list.isPending ? (
            <LoadingBlock lines={3} />
          ) : list.isError ? (
            <ErrorAlert error={list.error} onRetry={() => list.refetch()} />
          ) : (
            <ul className="divide-y divide-border">
              {items.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm">{entry.sender}</p>
                    {entry.autoGenerated ? (
                      <p className="text-xs text-muted-foreground">
                        このメールアドレス自身のため、自動で許可されています（削除できません）。
                      </p>
                    ) : entry.sender.startsWith('@') ? (
                      <p className="text-xs text-muted-foreground">
                        このドメインの任意のアドレスを差出人にできます。
                      </p>
                    ) : null}
                  </div>
                  {entry.autoGenerated || !canWrite ? null : (
                    <ConfirmDialogButton
                      title="この差出人の許可を外しますか？"
                      description={`${entry.sender} を差出人にした送信は、以後拒否されます。`}
                      confirmLabel="許可を外す"
                      pending={remove.isPending}
                      onConfirm={() => remove.mutate(entry.id)}
                    >
                      <Trash2 aria-hidden />
                      削除
                    </ConfirmDialogButton>
                  )}
                </li>
              ))}
            </ul>
          )}

          <FormAlert error={remove.error} />
        </CardContent>
      </Card>

      {canWrite ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">差出人を追加する</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!invalid) add.mutate();
              }}
            >
              <FormAlert error={add.error} />
              {add.isSuccess ? (
                <Alert variant="success">
                  <AlertDescription>
                    追加しました。メールサーバーへの反映まで数分かかることがあります。
                  </AlertDescription>
                </Alert>
              ) : null}

              <Field
                label="差出人アドレス"
                htmlFor="sender-acl-input"
                required
                hint={
                  <>
                    <code className="font-mono">info@example.jp</code> のように 1 件だけを指定するか、
                    <code className="mx-1 font-mono">@example.jp</code>
                    と書いてそのドメインの任意のアドレスを許可できます。検証が完了したお客様のドメインのみ指定できます。
                  </>
                }
              >
                <Input
                  id="sender-acl-input"
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  placeholder="info@example.jp"
                  autoComplete="off"
                />
              </Field>

              <SubmitBar
                submitLabel="追加する"
                pending={add.isPending}
                disabled={invalid}
              />
            </form>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------- メールソフト設定 */

function ClientsTab({ mailbox }: { mailbox: Mailbox }) {
  const org = useOrg();
  const [webmailError, setWebmailError] = React.useState<unknown>(null);

  const openWebmail = useMutation({
    mutationFn: () =>
      api.get<WebmailLink>(`/mailboxes/${encodeURIComponent(mailbox.id)}/webmail`),
    onError: (e) => setWebmailError(e),
  });

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Webメール</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {mailbox.webmailEnabled ? (
            <>
              <p className="text-sm text-muted-foreground">
                ブラウザでこのメールアドレスのメールを読み書きできます。
              </p>
              <Button
                onClick={() => {
                  // ポップアップブロック対策: クリックと同期的に空タブを開き、
                  // URL 取得後に遷移させる。
                  const tab = openPendingTab();
                  openWebmail.mutate(undefined, {
                    onSuccess: (link) => {
                      tab.navigate(link.url);
                    },
                    onError: () => tab.close(),
                  });
                }}
                loading={openWebmail.isPending}
              >
                <Mail aria-hidden />
                Webメールを開く
                <ExternalLink aria-hidden />
              </Button>
              {webmailError ? <ErrorAlert error={webmailError} /> : null}
              <WebmailPasswordPanel mailbox={mailbox} />
            </>
          ) : (
            <Alert variant="info">
              <AlertDescription>
                このメールアドレスでは Webメールが無効です。「基本設定」タブから有効にできます。
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {org.data ? (
        <>
          <MailClientTable host={org.data.node.mailHostname} address={mailbox.address} />
          <SetupSheetCallout mailboxId={mailbox.id} />
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">ログイン情報</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CopyableCode label="ユーザー名（ログイン ID）" value={mailbox.address} />
              <p className="text-xs text-muted-foreground">
                パスワードは「基本設定」タブから再設定できます（既存のパスワードは表示できません）。
              </p>
            </CardContent>
          </Card>
        </>
      ) : (
        <LoadingBlock lines={3} />
      )}

      <Button asChild variant="outline">
        <AppLink to="/aliases">
          <Plus aria-hidden />
          このドメインのエイリアスを追加する
        </AppLink>
      </Button>
    </div>
  );
}

/**
 * 設定情報シート（印刷して渡す紙）への導線（W-ONBOARD §3-4）。
 *
 * **画面のコピー欄とは別に紙を用意する理由。** 「メールソフトに何を入れれば
 * よいか分からない」人は、そもそもコントロールパネルにログインしない
 * （新入社員・退職者の後任・情シスに設定を任せる人）。値を画面でしか
 * 見られないと、管理者が電話やチャットでポート番号を読み上げることになり、
 * 読み間違いがそのまま二次問い合わせになる。
 *
 * **パスワードは印字されない**ことをここに書いておく。書いていないと
 * 「渡す前にパスワードを手書きで足す」運用が始まる。
 */
function SetupSheetCallout({ mailboxId }: { mailboxId: string }) {
  const href = `/print/mail-setup?ids=${encodeURIComponent(mailboxId)}`;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">設定情報を紙で渡す</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          このメールアドレスの接続情報を A4 1 枚にまとめて印刷できます。
          印刷ダイアログから PDF として保存することもできます。
          <span className="font-medium text-foreground">パスワードは印字されません。</span>
        </p>
        <Button asChild variant="outline">
          {/* 印刷画面は別タブ。同じタブだと戻ったときにタブの選択が消える。 */}
          <a href={href} target="_blank" rel="noopener noreferrer">
            <Printer aria-hidden />
            設定情報を印刷
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}
